import React, { useState, useEffect, useCallback, useRef } from 'react';
import { doc, getDoc, setDoc, getDocs, collection, deleteField, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import { formatTime12Hour } from '../../../utils/schedulingUtils';
// Horizontal placement for simultaneous lessons, plus the shared "HH:MM" parser
// so vertical (this file) and horizontal (that one) placement cannot disagree
// about when an occurrence starts.
import { assignLanes, toMinutes as toMin } from './weekLanes';
// The released rotation, read-only. `fetchLatestSchedule` is REUSED rather than
// re-queried: it owns `runForDate`, and CLAUDE.md requires that any new
// reader answering "what schedule is live?" go through that same selection rule
// or the release gate silently forks. This calendar therefore shows the
// RELEASED run — never the draft an admin is mid-edit on, which is what the
// rotation board is for.
import { fetchLatestSchedule } from '../../../services/teacherViewData';
import { buildRotationLookup, teacherIdForOccurrence } from '../../../services/rotationLookup';
import { lessonOccurrence, eventOccurrence, expandOnto } from '../../../utils/occurrenceExpansion';
import { fetchAssignedTimeSlots, updateTimeSlot, addTimeSlot } from '../../../services/timeSlotService';
import { fetchAllEvents, addEvent, updateEvent, deleteEvent } from '../../../services/eventService';
import { LOCATIONS, getLocationColor, MAKEUP_OUTLINE, CAL_CHROME, EVENT_TYPES, getEventColor } from './locations';
import { closureInfoFor, FIRST_LESSON_BY_WEEKDAY, SEASON_END } from '../../../data/seasonData';

// CalendarSystem rebuild. Renders the real `timeSlots` (status === 'assigned')
// in Day / Week / Month with the design's light-tint chip treatment, location
// filter, teal now-line, a persisted Monday/Sunday week start, and a detail
// drawer.
//
// Phase 2 (write side): Edit / Reschedule / Cancel + Undo are wired. A single
// occurrence of a recurring slot is overridden non-destructively via an
// `exceptions` map embedded on the existing timeSlots doc — keyed by the
// occurrence's origin date, holding either { cancelled:true } or a patch
// { startTime, endTime, instructor, location, date? } (a `date` means a
// cross-day move). All writes are client-side updateTimeSlot(); no new
// collections, Cloud Functions, or security-rule changes. timeSlots stays
// decoupled from the rotation engine — these edits never touch
// scheduleAssignments. teacherTimeOff is a soft warning only, never a block.
// (Add-lesson create form is the remaining Phase 2 piece.)

const SANS = "'Josefin Sans', system-ui, -apple-system, sans-serif";
const SERIF = "'Josefin Slab', Georgia, serif";

const DOW_SH = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DOW_FU = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MON_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MON_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const HOUR_START = 8;   // 8 AM
// 10 PM (last label row is 9 PM). WAS 21, WHICH CLIPPED REAL LESSONS: the grid
// floor is a hard bottom, and the studio runs 20:30 slots on four weeknights —
// Hunter Schweigert's Tuesday HLDC lesson is 20:30-21:15, so a quarter of its
// card was painted below the grid and cut off. Any floor at or below the last
// lesson END time silently truncates cards rather than scrolling to them.
// Verified against prod 2026-08-30: the latest slot in the season ends 21:15.
export const HOUR_END = 22;
const PXPM = 52 / 60;   // ~52px per hour
const GRID_TOP = HOUR_START * 60;
const GRID_H = (HOUR_END - HOUR_START) * 52; // 728

// ---- pure helpers ----
const fmt = (t) => formatTime12Hour(t);
const fmtHour = (hr) => (((hr + 11) % 12) + 1) + (hr >= 12 ? ' PM' : ' AM');
const fmtShort = (t) => { const [h, m] = t.split(':').map(Number); return (((h + 11) % 12) + 1) + (m ? ':' + String(m).padStart(2, '0') : '') + (h >= 12 ? 'p' : 'a'); };
const addDays = (dt, n) => { const x = new Date(dt); x.setDate(x.getDate() + n); return x; };
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
const parseYmd = (s) => new Date(s + 'T00:00:00');
const addMinStr = (v, add) => { const [h, m] = v.split(':').map(Number); const t = h * 60 + m + add; return String(Math.floor(t / 60)).padStart(2, '0') + ':' + String(t % 60).padStart(2, '0'); };
const hexA = (hex, a) => { const n = hex.replace('#', ''); return `rgba(${parseInt(n.slice(0, 2), 16)},${parseInt(n.slice(2, 4), 16)},${parseInt(n.slice(4, 6), 16)},${a})`; };
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
// Lesson length is per-instrument: voice 45, piano & guitar 30, private coaching 45.
const durationForInstrument = (instrument) => {
	const i = (instrument || '').toLowerCase();
	if (i.includes('piano') || i.includes('guitar')) return 30;
	return 45; // voice + private coaching (ad-hoc single session)
};
// Calendar v2: the lesson "category" is mapped onto the existing `instrument`
// field (no new field). Private Coaching = an ad-hoc EXTRA single session
// (audition / acting-piece prep), admin-created, single only.
const LESSON_CATEGORIES = ['Voice', 'Guitar', 'Piano', 'Private Coaching'];
const toCategory = (instr) => {
	const i = (instr || '').toLowerCase();
	if (i.includes('piano')) return 'Piano';
	if (i.includes('guitar')) return 'Guitar';
	if (i.includes('private')) return 'Private Coaching';
	return 'Voice';
};
// A recurring lesson belongs to a WEEKDAY COHORT, not to the day you happened to
// be looking at when you created it. Canon owns both ends of that cohort's run:
// firstLessonByWeekday when it starts, seasonEnd when it stops.
//
// FOUND 2026-08-12 (card LSJH6ru7wBwFjiybAtl7): the create form wrote
// `startDate: <the viewed date>`, so three lessons hand-added on Aug 6 began
// recurring that week rather than with the fall cohort — visible to families on
// the customer schedule — and carried endDate:null, so they would have recurred
// into the next season forever. Repaired by
// scripts/migration/fixRecurringSlotSeasonDates.js.
const DOW_CANON_KEY = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const canonStartFor = (dateStr) => {
	const d = parseYmd(dateStr);
	return FIRST_LESSON_BY_WEEKDAY[DOW_CANON_KEY[d.getDay()]] || dateStr;
};

const studentDisplayName = (s) => s.fullName || [s.firstName, s.lastName].filter(Boolean).join(' ') || s.name || s.studentName || 'Student';

const CalendarTab = () => {
	const { currentUser, userRole, userProfile } = useAuth();
	// Calendar v2: event create/edit/delete is admin/owner-only in v1 (matches the
	// firestore.rules `events` write gate). Lesson create stays open to any employee.
	const canManageEvents = userRole === 'admin' || userProfile?.owner === true;

	const [view, setView] = useState(() =>
		typeof window !== 'undefined' && window.innerWidth < 768 ? 'day' : 'week'
	);
	const [anchor, setAnchor] = useState(new Date());
	const [loc, setLoc] = useState('all');
	const [weekStart, setWeekStart] = useState('mon');
	const [prefsOpen, setPrefsOpen] = useState(false);
	// Phone breakpoint — matches the `.cyv` `max-width: 767px` layer used across
	// the portal. At this width the 7-column Week grid would force horizontal
	// scroll, so it collapses to the single-day agenda (see weekAsAgenda below).
	const [isNarrow, setIsNarrow] = useState(
		() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
	);
	// Narrow Month view only: the day whose agenda panel is open beneath the grid
	// (a `ymd` string, or null). Read nowhere else — Week/Day/desktop ignore it.
	const [selectedDay, setSelectedDay] = useState(null);
	const dayPanelRef = useRef(null);

	const [timeSlots, setTimeSlots] = useState([]);
	// Mirror of timeSlots for the useCallback write helpers (which close over an
	// empty dep list) so a failed write can roll the optimistic change back.
	const timeSlotsRef = useRef([]);
	const [events, setEvents] = useState([]);          // Calendar v2 portal-native events (all; low volume)
	const [teachers, setTeachers] = useState([]);
	// { byEvent, byDayLocation } from the released rotation, per date; null until loaded
	// or when the read is denied, in which case no teacher is shown at all.
	const [rotation, setRotation] = useState(null);
	const [rotationTeachers, setRotationTeachers] = useState({});
	const [timeOff, setTimeOff] = useState([]);
	const [students, setStudents] = useState([]);
	const [loading, setLoading] = useState(true);

	// drawer + write-mode state
	const [selected, setSelected] = useState(null); // occurrence object
	const [editing, setEditing] = useState(false);
	const [edit, setEdit] = useState(null);
	const [creating, setCreating] = useState(false);
	const [create, setCreate] = useState(null);
	const [cancelChoice, setCancelChoice] = useState(false);
	const [toast, setToast] = useState(null);
	const toastTimer = useRef(null);

	const today = new Date();
	const startDow = weekStart === 'sun' ? 0 : 1;
	// Week always renders the full 7-column grid — even at 375px, where it scrolls
	// horizontally (overflowX:auto on renderWeek) rather than collapsing to a
	// single-day agenda. The old phone collapse narrowed the visible window to one
	// day, which read as "only one lesson showing" (Week behaving like Day).
	// (Per Anthony 2026-06-28.) `isNarrow` still drives the compact Month grid.
	const weekAsAgenda = false;

	useEffect(() => {
		(async () => {
			setLoading(true);
			// Resilient load: each source is independent, so one denied/empty read
			// must NOT blank the rest. `events` in particular reads against a rules
			// block that isn't deployed until Anthony rides the firestore:rules
			// deploy — until then its read is denied, and an all-or-nothing
			// Promise.all would have nuked students / timeSlots / teachers too.
			// allSettled degrades each failure to "none of that type on the grid".
			const settle = (label, result, apply) => {
				if (result.status === 'fulfilled') apply(result.value);
				else console.error(`Calendar: ${label} failed to load (degrading to empty):`, result.reason);
			};
			const [slots, evts, tSnap, offSnap, stuSnap, runRes] = await Promise.allSettled([
				fetchAssignedTimeSlots(),
				fetchAllEvents(),
				getDocs(collection(db, 'teachers')),
				getDocs(collection(db, 'teacherTimeOff')),
				getDocs(collection(db, 'students')),
				// Staff-only read (viewSchedule), and allSettled for the same reason
				// as the reads above: a denial must cost the teacher NAMES, not the
				// calendar.
				fetchLatestSchedule(),
			]);
			settle('timeSlots', slots, (v) => setTimeSlots(v));
			settle('events', evts, (v) => setEvents(v));
			settle('teachers', tSnap, (snap) => setTeachers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
			settle('teacherTimeOff', offSnap, (snap) => setTimeOff(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
			settle('students', stuSnap, (snap) => setStudents(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
			settle('rotation', runRes, (v) => {
				setRotation(buildRotationLookup(v.assignments));
				setRotationTeachers(v.teachersById || {});
			});
			setLoading(false);
		})();
	}, []);

	// Load the teacher's persisted week-start preference.
	useEffect(() => {
		if (!currentUser) return;
		(async () => {
			try {
				const snap = await getDoc(doc(db, 'users', currentUser.uid));
				const pref = snap.exists() && snap.data().calendarWeekStart;
				if (pref === 'mon' || pref === 'sun') setWeekStart(pref);
			} catch (e) {
				console.error('Error loading calendar preference:', e);
			}
		})();
	}, [currentUser]);

	useEffect(() => { timeSlotsRef.current = timeSlots; }, [timeSlots]);

	useEffect(() => () => clearTimeout(toastTimer.current), []);

	// Bring the Month day-panel into view when a day is selected on a phone.
	useEffect(() => {
		if (selectedDay && dayPanelRef.current) {
			dayPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
	}, [selectedDay]);

	// Keep isNarrow in sync with viewport/orientation changes.
	useEffect(() => {
		if (typeof window === 'undefined') return undefined;
		const mq = window.matchMedia('(max-width: 767px)');
		const onChange = (e) => setIsNarrow(e.matches);
		mq.addEventListener('change', onChange);
		return () => mq.removeEventListener('change', onChange);
	}, []);

	const setWeekStartPref = useCallback(async (v) => {
		setWeekStart(v);
		if (!currentUser) return;
		try {
			await setDoc(doc(db, 'users', currentUser.uid), { calendarWeekStart: v }, { merge: true });
		} catch (e) {
			console.error('Error saving calendar preference:', e);
		}
	}, [currentUser]);

	// ---- exception-aware lesson lookup for a date ----
	// Occurrence semantics live in utils/occurrenceExpansion.js (shared with
	// the customer schedule view — one engine, two surfaces).

	// Unified read across the portal-native sources onto one grid: lessons
	// (timeSlots) + events. Each occurrence is tagged
	// `_kind` ('lesson' | event type) for render + edit-gating. Name
	// kept (`lessonsOn`) — many render paths call it — but it returns the merged
	// occurrence list. (The stale MMS `lessons` collection is a one-time Oct-2025
	// import being retired; it is not rendered.)
	const lessonsOn = useCallback((date) => {
		const dow = date.getDay();
		const dateStr = ymd(date);
		const out = [];
		timeSlots.forEach((slot) => expandOnto(slot, date, dateStr, dow, lessonOccurrence, out));
		events.forEach((ev) => expandOnto(ev, date, dateStr, dow, eventOccurrence, out));
		const filtered = loc === 'all' ? out : out.filter((s) => s.location === loc);
		return filtered.sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
	}, [timeSlots, events, loc]);

	const weekStartOf = (dt) => addDays(dt, -(((dt.getDay() - startDow) + 7) % 7));
	const wkStart = weekStartOf(anchor);

	const navigate = (dir) => {
		const d = new Date(anchor);
		if (view === 'week' && !weekAsAgenda) d.setDate(d.getDate() + dir * 7);
		else if (view === 'month') {
			// Anchor to the 1st BEFORE shifting months so a 29–31 day never
			// overflows into the wrong month (e.g. Jan 31 + 1mo → Mar 3, skipping
			// Feb). Month view only reads year+month off the anchor, so
			// normalizing the day is safe.
			d.setDate(1);
			d.setMonth(d.getMonth() + dir);
		}
		else d.setDate(d.getDate() + dir);
		setAnchor(d);
		// The selected day may no longer be on screen after a month change.
		if (view === 'month') setSelectedDay(null);
	};

	// ===== write helpers (embedded exceptions map) =====
	const writeException = useCallback(async (slotId, dateStr, value) => {
		// Snapshot the prior exception value so a failed write can be rolled back
		// (the callback closes over [] deps, so read live state via the ref).
		const curSlot = timeSlotsRef.current.find((s) => s.id === slotId);
		const priorEx = (curSlot && curSlot.exceptions) || {};
		const hadKey = Object.prototype.hasOwnProperty.call(priorEx, dateStr);
		const priorVal = priorEx[dateStr];

		// optimistic local update
		setTimeSlots((prev) => prev.map((s) => {
			if (s.id !== slotId) return s;
			const ex = { ...(s.exceptions || {}) };
			if (value === null) delete ex[dateStr]; else ex[dateStr] = value;
			return { ...s, exceptions: ex };
		}));
		try {
			await updateTimeSlot(slotId, {
				[`exceptions.${dateStr}`]: value === null ? deleteField() : value,
				updatedAt: serverTimestamp(),
			});
		} catch (e) {
			console.error('Error saving lesson change:', e);
			// Roll the optimistic change back so the grid matches what persisted,
			// then rethrow so callers can surface an error instead of a false
			// success toast (#11a).
			setTimeSlots((prev) => prev.map((s) => {
				if (s.id !== slotId) return s;
				const ex = { ...(s.exceptions || {}) };
				if (hadKey) ex[dateStr] = priorVal; else delete ex[dateStr];
				return { ...s, exceptions: ex };
			}));
			throw e;
		}
	}, []);

	const flashToast = (t) => {
		setToast(t);
		clearTimeout(toastTimer.current);
		toastTimer.current = setTimeout(() => setToast((cur) => (cur === t ? null : cur)), 4500);
	};

	const closeDrawer = () => { setSelected(null); setEditing(false); setCreating(false); setCreate(null); setCancelChoice(false); };

	const startEdit = () => {
		setCancelChoice(false);
		const s = selected;
		const dur = toMin(s.endTime) - toMin(s.startTime);
		setEdit({
			slotId: s._slotId, originDate: s._originDate, date: ymd(s._date),
			student: s.studentName, location: s.location, instructor: s.instructor || '',
			instrument: toCategory(s.instrument), startTime: s.startTime, endTime: s.endTime, dur, isMakeup: s.isMakeup,
		});
		setEditing(true);
	};
	const editSet = (patch) => setEdit((e) => ({ ...e, ...patch }));
	// Edit-form category change: re-derive duration + end time (per-occurrence).
	const onEditCategory = (cat) => {
		const dur = durationForInstrument(cat);
		setEdit((e) => ({ ...e, instrument: cat, dur, endTime: addMinStr(e.startTime, dur) }));
	};

	const saveEdit = async () => {
		const e = edit;
		const patch = { startTime: e.startTime, endTime: e.endTime, instructor: e.instructor, location: e.location, instrument: e.instrument };
		if (e.date && e.date !== e.originDate) patch.date = e.date;
		try {
			await writeException(e.slotId, e.originDate, patch);
		} catch (err) {
			flashToast({ id: 'err', msg: 'That change didn’t save. Please try again.' });
			return;
		}
		setSelected((sel) => sel ? { ...sel, startTime: e.startTime, endTime: e.endTime, instructor: e.instructor, location: e.location, instrument: e.instrument, _date: parseYmd(e.date) } : null);
		setEditing(false);
		flashToast({ id: 'e', msg: `${e.student}’s lesson updated` });
	};

	const cancelLesson = async () => {
		const s = selected;
		const originDate = s._originDate;
		try {
			await writeException(s._slotId, originDate, { cancelled: true });
		} catch (err) {
			flashToast({ id: 'err', msg: 'That cancellation didn’t save. Please try again.' });
			return;
		}
		closeDrawer();
		flashToast({ id: 'c', msg: `${s.studentName}’s lesson cancelled`, undoId: `${s._slotId}|${originDate}` });
	};

	const undoCancel = async (undoId) => {
		const [slotId, dateStr] = undoId.split('|');
		clearTimeout(toastTimer.current);
		try {
			await writeException(slotId, dateStr, null);
			setToast(null);
		} catch (err) {
			flashToast({ id: 'err', msg: 'Couldn’t undo that just now. Please try again.' });
		}
	};

	// Stop a recurring slot going forward by end-dating it the day before this
	// occurrence — a soft, employee-permitted update (not a delete). Past
	// lessons are kept; this and all future occurrences drop out.
	const cancelSeries = async () => {
		const s = selected;
		if (!window.confirm(`Cancel ${s.studentName}’s lesson and all future weekly occurrences? Past lessons are kept. (A full series delete is admin-only.)`)) return;
		const endStr = ymd(addDays(parseYmd(s._originDate), -1));
		const prior = timeSlots.find((t) => t.id === s._slotId);
		const priorEnd = prior ? prior.endDate : undefined;
		setTimeSlots((prev) => prev.map((t) => (t.id === s._slotId ? { ...t, endDate: endStr } : t)));
		try {
			await updateTimeSlot(s._slotId, { endDate: endStr, updatedAt: serverTimestamp() });
		} catch (e) {
			console.error('Error cancelling series:', e);
			setTimeSlots((prev) => prev.map((t) => (t.id === s._slotId ? { ...t, endDate: priorEnd } : t)));
			flashToast({ id: 'err', msg: 'That change didn’t save. Please try again.' });
			return;
		}
		closeDrawer();
		flashToast({ id: 'cs', msg: `${s.studentName}’s weekly lessons cancelled going forward` });
	};

	// ===== Add lesson (create) =====
	const defaultCreateDate = () => ymd(view === 'month' && selectedDay ? parseYmd(selectedDay) : ((view === 'day' || weekAsAgenda) ? anchor : today));
	const openCreate = () => {
		setCreate({
			kind: 'lesson',
			studentId: '', studentName: '', instrument: 'Voice', // category default — mapped onto `instrument`
			location: 'Lakeville', instructor: '', // default Unassigned — spots aren't instructor-assigned by default
			date: defaultCreateDate(), startTime: '15:45', duration: durationForInstrument('Voice'), isMakeup: false,
			repeat: false, // "Repeat weekly" toggle (OFF → single write, exactly as before)
			// Only read when `repeat` is on. Defaults to the weekday cohort's canon
			// first-lesson date rather than the viewed day — editable, because a
			// mid-season joiner really does start later.
			startDate: canonStartFor(defaultCreateDate()),
		});
		setSelected(null); setEditing(false); setCreating(true);
	};
	const createSet = (patch) => setCreate((c) => ({ ...c, ...patch }));
	// Changing the date changes the WEEKDAY, and therefore which cohort the
	// series belongs to — so the canon default follows it. A hand-edited
	// startDate survives until the date moves again, which is the predictable
	// behavior; silently keeping a Monday start on a slot just moved to Thursday
	// is not.
	const onCreateDate = (date) => setCreate((c) => ({ ...c, date, startDate: canonStartFor(date) }));
	// Category picker = the lesson `instrument` (widened): Voice / Guitar / Piano /
	// Private Coaching. Duration auto-derives; Private Coaching is single-only, so
	// picking it clears the recurring toggle.
	const onPickCategory = (cat) => {
		const dur = durationForInstrument(cat);
		setCreate((c) => ({ ...c, instrument: cat, duration: dur, endTime: addMinStr(c.startTime, dur), repeat: cat === 'Private Coaching' ? false : c.repeat }));
	};
	const onPickStudent = (id) => {
		const stu = students.find((s) => s.id === id);
		if (!stu) { createSet({ studentId: '', studentName: '' }); return; }
		const cat = toCategory(stu.instrument);
		const dur = durationForInstrument(cat);
		setCreate((c) => ({ ...c, studentId: id, studentName: studentDisplayName(stu), instrument: cat, duration: dur, endTime: addMinStr(c.startTime, dur) }));
	};
	const saveCreate = async () => {
		const c = create;
		if (!c.studentId) return;
		const d = parseYmd(c.date);
		// Recurring only when the toggle is on AND the category allows it (make-up
		// and Private Coaching are one-off single sessions).
		const recurring = !!c.repeat && !c.isMakeup && c.instrument !== 'Private Coaching';
		const base = {
			studentId: c.studentId, studentName: c.studentName,
			instrument: c.instrument, // Calendar v2: the category is stored here
			location: c.location, instructor: c.instructor,
			startTime: c.startTime, endTime: addMinStr(c.startTime, c.duration), duration: c.duration,
			dayOfWeek: d.getDay(), isMakeup: !!c.isMakeup, status: 'assigned',
			createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
		};
		const docData = recurring
			// startDate is the cohort's, NOT the viewed date; endDate stops the
			// series at the end of the season instead of recurring forever.
			? { ...base, isRecurring: true, startDate: c.startDate || canonStartFor(c.date), endDate: SEASON_END }
			: { ...base, isRecurring: false, specificDate: c.date };
		try {
			const ref = await addTimeSlot(docData);
			setTimeSlots((prev) => [...prev, { id: ref.id, ...docData }]);
		} catch (e) {
			console.error('Error adding lesson:', e);
			flashToast({ id: 'err', msg: 'That lesson didn’t save. Please try again.' });
			return;
		}
		closeDrawer();
		flashToast({ id: 'a', msg: `${c.studentName}’s ${recurring ? 'weekly lesson' : 'lesson'} added` });
	};

	// ===== Events (group class / rehearsal / recital) — admin/owner only =====
	const recitalEvents = events.filter((e) => e.type === 'recital');

	const openCreateEvent = () => {
		setCreate({
			kind: 'event', type: 'groupClass', title: '',
			location: 'Lakeville', date: defaultCreateDate(), startTime: '16:00', endTime: '17:00',
			repeat: false,
			blocksTimeOff: false,              // must-work toggle; recital flips it on
			capacity: '', roster: [],          // groupClass
			cast: [], recitalId: '',           // rehearsal
			venue: '', callTimes: [],          // recital
		});
		setSelected(null); setEditing(false); setCreating(true);
	};

	// Assemble the type-specific + occurrence fields shared by create and edit.
	const eventFieldsFrom = (c, { forCreate }) => {
		const d = parseYmd(c.date);
		// Only group classes may recur; rehearsals + recitals are single-only (v1).
		const recurring = c.type === 'groupClass' && !!c.repeat;
		const occFields = forCreate
			? (recurring ? { isRecurring: true, startDate: c.date, endDate: null } : { isRecurring: false, specificDate: c.date })
			: (c.isRecurring ? {} : { specificDate: c.date }); // edit: keep occurrence kind; single may move date
		let typeFields = {};
		if (c.type === 'groupClass') typeFields = { location: c.location, roster: c.roster || [], capacity: c.capacity === '' ? null : Number(c.capacity) };
		else if (c.type === 'rehearsal') typeFields = { location: c.location, cast: c.cast || [], recitalId: c.recitalId || null };
		else if (c.type === 'recital') typeFields = { location: null, venue: (c.venue || '').trim(), callTimes: (c.callTimes || []).filter((ct) => ct.label || ct.time) };
		return {
			// blocksTimeOff is stored as an EXPLICIT boolean so the toggle is
			// what-you-see-is-what's-enforced. Server default for a doc with NO
			// field: recitals block, others don't (addTeacherTimeOff core) —
			// which covers legacy events created before this toggle existed.
			fields: { type: c.type, title: (c.title || '').trim(), startTime: c.startTime, endTime: c.endTime, dayOfWeek: d.getDay(), blocksTimeOff: !!c.blocksTimeOff, ...occFields, ...typeFields },
			recurring,
		};
	};

	const saveCreateEvent = async () => {
		const c = create;
		if (!(c.title || '').trim()) return;
		const { fields } = eventFieldsFrom(c, { forCreate: true });
		const docData = { ...fields, status: 'active', exceptions: {}, createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
		try {
			const ref = await addEvent(docData);
			setEvents((prev) => [...prev, { id: ref.id, ...docData }]);
		} catch (e) {
			console.error('Error adding event:', e);
			flashToast({ id: 'err', msg: 'That event didn’t save. Please try again.' });
			return;
		}
		closeDrawer();
		flashToast({ id: 'ev', msg: `${docData.title || 'Event'} added` });
	};

	const startEditEvent = () => {
		setCancelChoice(false);
		const s = selected;
		setEdit({
			kind: 'event', eventId: s._eventId, type: s._kind, title: s.title || '',
			location: s.location || 'Lakeville', date: s.specificDate || ymd(s._date),
			startTime: s.startTime, endTime: s.endTime,
			isRecurring: !!s.isRecurring,
			blocksTimeOff: s.blocksTimeOff === undefined ? s._kind === 'recital' : s.blocksTimeOff === true,
			capacity: s.capacity == null ? '' : String(s.capacity), roster: s.roster || [],
			cast: s.cast || [], recitalId: s.recitalId || '',
			venue: s.venue || '', callTimes: s.callTimes || [],
		});
		setEditing(true);
	};

	const saveEditEvent = async () => {
		const e = edit;
		const { fields } = eventFieldsFrom(e, { forCreate: false });
		// A recurring event recurs by weekday (dayOfWeek). If the edited date now
		// lands on a different weekday, saving moves the ENTIRE series — confirm
		// that explicitly rather than moving it silently (#11b).
		if (e.isRecurring) {
			const existing = events.find((ev) => ev.id === e.eventId);
			if (existing && fields.dayOfWeek !== existing.dayOfWeek) {
				const wd = DOW_FU[fields.dayOfWeek];
				if (!window.confirm(`This moves the entire "${fields.title || 'event'}" series to every ${wd}. Continue?`)) return;
			}
		}
		const prior = events;
		setEvents((prev) => prev.map((ev) => (ev.id === e.eventId ? { ...ev, ...fields } : ev)));
		try {
			await updateEvent(e.eventId, { ...fields, updatedAt: serverTimestamp() });
		} catch (err) {
			console.error('Error updating event:', err);
			setEvents(prior);
			flashToast({ id: 'err', msg: 'That event didn’t save. Please try again.' });
			return;
		}
		setEditing(false);
		closeDrawer();
		flashToast({ id: 'eu', msg: `${fields.title || 'Event'} updated` });
	};

	// Stop a recurring event going forward (end-date the day before this
	// occurrence) — mirrors the lesson cancelSeries soft-stop. Past occurrences kept.
	const stopEventSeries = async () => {
		const s = selected;
		if (!window.confirm(`End "${s.title}" and all future weekly occurrences? Past dates are kept. (Deleting the whole series is the other option.)`)) return;
		const endStr = ymd(addDays(parseYmd(s._originDate), -1));
		const prior = events;
		setEvents((prev) => prev.map((ev) => (ev.id === s._eventId ? { ...ev, endDate: endStr } : ev)));
		try {
			await updateEvent(s._eventId, { endDate: endStr, updatedAt: serverTimestamp() });
		} catch (e) {
			console.error('Error ending event series:', e);
			setEvents(prior);
			flashToast({ id: 'err', msg: 'That change didn’t save. Please try again.' });
			return;
		}
		closeDrawer();
		flashToast({ id: 'es', msg: `${s.title} ended going forward` });
	};

	// True delete — whole event (single) or whole series (recurring).
	const removeEvent = async () => {
		const s = selected;
		if (!window.confirm(`Delete "${s.title}"${s.isRecurring ? ' and ALL its weekly occurrences' : ''}? This can't be undone.`)) return;
		const prior = events;
		setEvents((prev) => prev.filter((ev) => ev.id !== s._eventId));
		try {
			await deleteEvent(s._eventId);
		} catch (e) {
			console.error('Error deleting event:', e);
			setEvents(prior);
			flashToast({ id: 'err', msg: 'That event couldn’t be deleted. Please try again.' });
			return;
		}
		closeDrawer();
		flashToast({ id: 'ed', msg: `${s.title} deleted` });
	};

	// Instructor options: all real teaching artists (from the `teachers`
	// collection), unioned with any free-text instructor names already present on
	// existing slots. Lessons aren't instructor-assigned by default, so the picker
	// defaults to Unassigned (the "" option in the selects) rather than a teacher.
	const instructorOptions = (() => {
		const set = new Set(teachers.map((t) => t.name).filter(Boolean));
		timeSlots.forEach((s) => { if (s.instructor) set.add(s.instructor); });
		if (edit && edit.instructor) set.add(edit.instructor);
		return Array.from(set).sort();
	})();

	// soft, non-blocking time-off warning (best-effort name -> teacher match)
	const offWarn = (instrName, dateStr) => {
		if (!instrName || !dateStr) return null;
		const t = teachers.find((tt) => tt.name && tt.name.toLowerCase().includes(instrName.toLowerCase()));
		if (!t) return null;
		return timeOff.some((o) => o.teacherId === t.id && o.date === dateStr)
			? `Heads up: ${instrName} has time off on this date.` : null;
	};

	// ===== date label + summary =====
	let dateLabel;
	if (view === 'week' && !weekAsAgenda) {
		const a = wkStart, b = addDays(wkStart, 6);
		dateLabel = a.getMonth() === b.getMonth()
			? `${MON_ABBR[a.getMonth()]} ${a.getDate()} – ${b.getDate()}, ${b.getFullYear()}`
			: `${MON_ABBR[a.getMonth()]} ${a.getDate()} – ${MON_ABBR[b.getMonth()]} ${b.getDate()}, ${b.getFullYear()}`;
	} else if (view === 'month') {
		dateLabel = `${MON_FULL[anchor.getMonth()]} ${anchor.getFullYear()}`;
	} else {
		dateLabel = `${DOW_FU[anchor.getDay()]}, ${MON_FULL[anchor.getMonth()]} ${anchor.getDate()}`;
	}

	const my = anchor.getFullYear(), mm = anchor.getMonth();
	const daysInMonth = new Date(my, mm + 1, 0).getDate();
	let scope = [];
	if (view === 'week' && !weekAsAgenda) for (let i = 0; i < 7; i++) scope.push(addDays(wkStart, i));
	else if (view === 'month') for (let d = 1; d <= daysInMonth; d++) scope.push(new Date(my, mm, d));
	else scope.push(anchor);
	const scopeOccs = scope.flatMap((dt) => lessonsOn(dt));
	const lessonOccs = scopeOccs.filter((s) => s._kind === 'lesson');
	const eventCount = scopeOccs.length - lessonOccs.length;
	const countAll = lessonOccs.length;
	const countLak = lessonOccs.filter((s) => s.location === 'Lakeville').length;
	const scopeWord = (view === 'week' && !weekAsAgenda) ? 'this week' : (view === 'month' ? 'this month' : 'today');
	const summary = `${countAll} lesson${countAll === 1 ? '' : 's'} ${scopeWord}  ·  ${countLak} Lakeville  ·  ${countAll - countLak} Headline${eventCount ? `  ·  ${eventCount} event${eventCount === 1 ? '' : 's'}` : ''}`;

	// ===== chip renderers =====
	// Occurrence → color/label resolvers. Lessons color by location; events color
	// by type.
	// Resolve the accent ONCE rather than at each render site: lessons color by
	// location, events by type. Every chip / dot / drawer path goes through these.
	const occAccent = (s) => getEventColor(s._kind);
	const occColor = (s) => (s._kind === 'lesson' ? getLocationColor(s.location) : occAccent(s));
	const occLabel = (s) => (s._kind === 'lesson' ? s.studentName : (s.title || getEventColor(s._kind).name));

	// Rotation teacher for one occurrence, as a display name. LESSONS ONLY:
	// events are not in the rotation, and borrowing the day's teacher
	// for them would assert something nobody scheduled. null => render nothing,
	// which is deliberately different from "Unassigned".
	const rotationTeacherName = (s, dateStr) => {
		if (!rotation || s._kind !== 'lesson') return null;
		const id = teacherIdForOccurrence(rotation, s, dateStr);
		return id ? rotationTeachers[id] || null : null;
	};

	const weekChip = (s, key, dateStr, lane = 0, lanes = 1) => {
		const c = occColor(s);
		const label = occLabel(s);
		const isEvt = s._kind !== 'lesson';
		const top = (toMin(s.startTime) - GRID_TOP) * PXPM;
		const ht = Math.max((toMin(s.endTime) - toMin(s.startTime)) * PXPM, 30);
		// One lane => `left: calc(0% + 5px); width: calc(100% - 10px)`, which is
		// exactly the old `left:5; right:5`. So the overwhelmingly common
		// single-lesson case renders as before and only genuine collisions change
		// shape. Padding tightens when sharing, because a half-width column has
		// ~37px of usable text at the grid's 720px minimum.
		const st = {
			position: 'absolute',
			left: `calc(${(lane * 100) / lanes}% + 5px)`,
			width: `calc(${100 / lanes}% - 10px)`,
			top, height: ht, borderRadius: 9,
			padding: lanes > 1 ? (ht < 40 ? '2px 5px' : '5px 5px') : (ht < 40 ? '2px 9px' : '5px 9px'),
			boxSizing: 'border-box', overflow: 'hidden',
			background: c.fill, color: c.text, border: '1px solid ' + hexA(c.bar, 0.4),
			borderLeft: '4px solid ' + c.bar, cursor: 'pointer',
		};
		if (s.isMakeup) Object.assign(st, { outline: '2px dashed ' + MAKEUP_OUTLINE, outlineOffset: -3 });
		const who = rotationTeacherName(s, dateStr);
		// The tooltip stays the complete answer, because a half-width card at the
		// 720px minimum ellipsises almost anything.
		const title = isEvt
			? `${label} · ${occAccent(s).name} · ${fmt(s.startTime)}`
			: `${label} · ${fmt(s.startTime)}–${fmt(s.endTime)} · ${getLocationColor(s.location).name}${who ? ` · ${who}` : ''}`;
		// The end time is worth its width only on a tall, full-width card; on
		// anything tighter the start time plus the teacher is the information
		// actually being asked for.
		const roomy = ht >= 46 && lanes === 1;
		const meta = [
			roomy ? `${fmt(s.startTime)}–${fmt(s.endTime)}` : fmt(s.startTime),
			s.isMakeup ? 'Make-up' : null,
			who,
			isEvt && s._kind === 'groupClass' && s.capacity ? `${(s.roster || []).length}/${s.capacity}` : null,
		].filter(Boolean).join(' · ');
		return (
			<div key={key} onClick={() => { setSelected(s); setEditing(false); setCancelChoice(false); }} title={title} style={st}>
				<div style={{ fontWeight: 700, fontSize: '11.5px', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 5 }}>
					{isEvt && <i className={`fa-sharp fa-solid ${occAccent(s).icon}`} style={{ fontSize: 9.5, flex: '0 0 auto' }} />}
					<span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
				</div>
				{/* ALWAYS rendered. This used to be gated on `ht >= 40`, which is
				    ~46 minutes at the current scale — so every 30-minute lesson
				    displayed as a bare name with NO TIME AT ALL, on a grid whose
				    entire job is telling you when things are. Same family as the
				    hover-only rule in CLAUDE.md: information that appears only at
				    certain sizes is not an affordance. Short cards tighten the
				    line-height instead of dropping the line. */}
				<div style={{ fontSize: 10, lineHeight: ht < 40 ? 1.1 : 1.3, marginTop: ht < 40 ? 0 : 1, opacity: 0.92, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
					{meta}
				</div>
			</div>
		);
	};

	const monthChip = (s, key) => {
		const c = occColor(s);
		const label = occLabel(s);
		const isEvt = s._kind !== 'lesson';
		const st = {
			display: 'flex', alignItems: 'center', gap: 5, borderRadius: 5, padding: '2px 6px', marginTop: 3,
			fontSize: '10.5px', fontWeight: 600, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden',
			textOverflow: 'ellipsis', background: c.fill, color: c.text, borderLeft: '3px solid ' + c.bar, cursor: 'pointer',
		};
		if (s.isMakeup) Object.assign(st, { outline: '1.5px dashed ' + MAKEUP_OUTLINE, outlineOffset: -2 });
		return (
			<div key={key} onClick={() => { setSelected(s); setEditing(false); setCancelChoice(false); }} style={st}>
				{isEvt && <i className={`fa-sharp fa-solid ${occAccent(s).icon}`} style={{ fontSize: 8.5, flex: '0 0 auto' }} />}
				<span style={{ fontWeight: 700, opacity: 0.8 }}>{fmtShort(s.startTime)}</span>
				<span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
			</div>
		);
	};

	// ===== season-day markers (canon-driven) =====
	// Closures + recital days suppress every lesson (occurrenceExpansion bails on
	// a closed date), so the grid would otherwise show an unexplained empty day.
	// Surface WHY: recitals read as the gold event (reuse EVENT_TYPES recital),
	// closures as a muted "Closed · <name>". Flex/reserve days still hold lessons
	// — a subtle staff-only "Flex" tag flags them (never parent-facing).
	const RECITAL_STYLE = getEventColor('recital');
	// Cell background tint for a closed day (null → not closed).
	const closedCellBg = (info) => (info ? (info.type === 'recital' ? RECITAL_STYLE.fill : CAL_CHROME.closedBg) : null);

	// Compact in-cell marker for a month day cell.
	const monthClosedMarker = (info, key) => {
		const recital = info.type === 'recital';
		const st = {
			display: 'flex', alignItems: 'center', gap: 5, borderRadius: 5, padding: '2px 6px', marginTop: 3,
			fontSize: '10.5px', fontWeight: 700, lineHeight: 1.35, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
			background: recital ? RECITAL_STYLE.fill : 'transparent',
			color: recital ? RECITAL_STYLE.text : CAL_CHROME.closedText,
			border: recital ? '1px solid ' + hexA(RECITAL_STYLE.bar, 0.4) : 'none',
		};
		return (
			<div key={key} title={info.name + (info.location ? ' · ' + info.location : '')} style={st}>
				{recital && <i className="fa-sharp fa-solid fa-star" style={{ fontSize: 8.5, flex: '0 0 auto' }} />}
				<span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{recital ? info.name : 'Closed · ' + info.name}</span>
			</div>
		);
	};

	// Staff-only "Reserve" tag. No recurring lesson renders on a flex day — the
	// family is shown it as closed — but staff CAN still place a makeup here,
	// which is the difference between a flex day and a real closure.
	const flexTag = (key) => (
		<div key={key} title="Reserve day — makeups only, no regular lessons" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: CAL_CHROME.flexText, marginTop: 3, textAlign: 'center' }}>Reserve</div>
	);

	// closureInfoFor returns flex days too (families read them as plain closed
	// days). Staff need the two apart: a closure is dark, a reserve day still
	// takes makeups. Splitting it here keeps `closure` meaning "really closed"
	// throughout the calendar chrome below.
	const dayMarker = (dateStr) => {
		const info = closureInfoFor(dateStr);
		const flex = !!info && info.type === 'flex';
		return { closure: flex ? null : info, flex };
	};

	// ===== WEEK =====
	const renderWeek = () => {
		const hours = [];
		for (let hr = HOUR_START; hr < HOUR_END; hr++) hours.push(fmtHour(hr));
		const nowMin = today.getHours() * 60 + today.getMinutes();
		return (
			<div style={{ overflowX: 'auto' }}>
				<div style={{ display: 'flex', border: '1px solid #ece7e0', borderRadius: 13, overflow: 'hidden', minWidth: 720 }}>
					<div style={{ width: 64, flex: '0 0 64px', background: '#fcfaf7' }}>
						<div style={{ height: 56, borderBottom: '1px solid #ece7e0' }} />
						{hours.map((h, i) => (
							<div key={i} style={{ height: 52, borderTop: '1px solid #f0ece6', fontSize: '10.5px', color: '#a6adba', fontWeight: 500, textAlign: 'right', padding: '4px 9px 0 0' }}>{h}</div>
						))}
					</div>
					{[...Array(7)].map((_, i) => {
						const dt = addDays(wkStart, i);
						const td = sameDay(dt, today);
						const { closure, flex } = dayMarker(ymd(dt));
						// Lane ONCE per day and render from the same array, so the
						// Map's keys line up with the indices used for React keys.
						const dayOccs = lessonsOn(dt);
						const placement = assignLanes(dayOccs);
						const chips = dayOccs.map((s, j) => {
							const at = placement.get(j) || { lane: 0, lanes: 1 };
							return weekChip(s, `w${i}-${j}`, ymd(dt), at.lane, at.lanes);
						});
						const headBg = closure ? closedCellBg(closure) : (td ? CAL_CHROME.todayBg : '#fff');
						return (
							<div key={i} style={{ flex: 1, minWidth: 0, borderLeft: '1px solid #f0ece6' }}>
								<div style={{ height: 56, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2, borderBottom: '1px solid ' + CAL_CHROME.headerLine, background: headBg }}>
									<div style={{ fontSize: '10.5px', fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', fontFamily: SERIF, color: td ? CAL_CHROME.todayText : '#9aa2b1' }}>{DOW_SH[dt.getDay()]}</div>
									<div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1, fontFamily: SERIF, color: td ? CAL_CHROME.todayText : '#384971' }}>{dt.getDate()}</div>
									{flex && <div title="Reserve / flex day — lessons run normally" style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: CAL_CHROME.flexText, lineHeight: 1 }}>Flex</div>}
								</div>
								<div style={{ position: 'relative', height: GRID_H, background: closure ? hexA(closure.type === 'recital' ? RECITAL_STYLE.bar : CAL_CHROME.closedText, 0.05) : undefined }}>
									{hours.map((_, k) => (<div key={k} style={{ height: 52, borderTop: '1px solid #f6f2ec' }} />))}
									{closure && (
										<div style={{ position: 'absolute', top: 10, left: 6, right: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, textAlign: 'center', color: closure.type === 'recital' ? RECITAL_STYLE.text : CAL_CHROME.closedText, pointerEvents: 'none' }}>
											<i className={`fa-sharp fa-solid ${closure.type === 'recital' ? 'fa-star' : 'fa-calendar-xmark'}`} style={{ fontSize: 13 }} />
											<span style={{ fontSize: 11, fontWeight: 700, lineHeight: 1.25 }}>{closure.type === 'recital' ? closure.name : 'Studio closed'}</span>
											<span style={{ fontSize: 10, fontWeight: 500, lineHeight: 1.25, opacity: 0.85 }}>{closure.type === 'recital' ? (closure.location || 'No regular lessons') : closure.name}</span>
										</div>
									)}
									{chips}
									{td && nowMin >= GRID_TOP && nowMin <= HOUR_END * 60 && (
										<div style={{ position: 'absolute', left: 0, right: 0, top: (nowMin - GRID_TOP) * PXPM, height: 0, borderTop: '2px solid ' + CAL_CHROME.todayText, zIndex: 5 }}>
											<div style={{ position: 'absolute', left: -4, top: -4, width: 8, height: 8, borderRadius: '50%', background: CAL_CHROME.todayText }} />
										</div>
									)}
								</div>
							</div>
						);
					})}
				</div>
			</div>
		);
	};

	// ===== MONTH =====
	const renderMonth = () => {
		const first = new Date(my, mm, 1);
		const offset = ((first.getDay() - startDow) + 7) % 7;
		const gridStart = addDays(first, -offset);
		const totalCells = Math.ceil((offset + daysInMonth) / 7) * 7;
		const weeks = [];
		for (let i = 0; i < totalCells; i++) {
			const dt = addDays(gridStart, i);
			const inMonth = dt.getMonth() === mm;
			const td = sameDay(dt, today);
			const marker = inMonth ? dayMarker(ymd(dt)) : { closure: null, flex: false };
			const { closure } = marker;
			const flex = marker.flex;
			const slots = inMonth ? lessonsOn(dt) : [];
			const dayBg = !inMonth ? CAL_CHROME.outBg : (closure ? closedCellBg(closure) : (td ? CAL_CHROME.todayBg : '#fff'));
			const numCol = !inMonth ? '#c4c8d0' : (td ? CAL_CHROME.todayText : '#384971');
			let cell;
			if (isNarrow) {
				// Phone: a compact cell so all 7 columns fit (no sideways scroll).
				// Lessons render as location-colored dots instead of text chips;
				// tapping an in-month day opens that day's agenda.
				const MAX_DOTS = 4;
				// Tap a day → toggle its agenda panel beneath the grid (stay in Month).
				// Tapping the open day again closes it. No more flip to the Day view.
				const isSel = inMonth && selectedDay === ymd(dt);
				const openDay = inMonth
					? () => setSelectedDay((cur) => (cur === ymd(dt) ? null : ymd(dt)))
					: undefined;
				cell = (
					<div key={'c' + i} onClick={openDay} style={{ flex: 1, minWidth: 0, minHeight: 54, borderRight: '1px solid ' + CAL_CHROME.cellBorder, borderBottom: '1px solid ' + CAL_CHROME.cellBorder, padding: '5px 3px 7px', boxSizing: 'border-box', background: dayBg, boxShadow: isSel ? 'inset 0 0 0 2px ' + CAL_CHROME.todayText : undefined, cursor: openDay ? 'pointer' : 'default' }}>
						<div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
							<div style={{ minWidth: 20, height: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 999, fontWeight: 700, fontSize: '11.5px', fontFamily: SERIF, color: td ? '#fff' : numCol, background: td ? CAL_CHROME.todayText : 'transparent' }}>{dt.getDate()}</div>
						</div>
						{/* Closure marker AND the dots, not one or the other. These were
						    exclusive branches, which was harmless while a closed day could
						    hold nothing — until events stopped being suppressed on closed
						    days (2026-08-06). A recital day now carries the recital EVENT,
						    and hiding its dot here would leave the phone grid saying
						    "Recital" with no way to see there is a record behind it. */}
						{closure && (
							<div title={closure.name + (closure.location ? ' · ' + closure.location : '')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, fontSize: 8.5, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: closure.type === 'recital' ? RECITAL_STYLE.text : CAL_CHROME.closedText, lineHeight: 1.1, padding: '0 2px', overflow: 'hidden' }}>
								{closure.type === 'recital' && <i className="fa-sharp fa-solid fa-star" style={{ fontSize: 7.5, flex: '0 0 auto' }} />}
								<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{closure.type === 'recital' ? 'Recital' : 'Closed'}</span>
							</div>
						)}
						{(!closure || slots.length > 0) && (
							<div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignContent: 'flex-start', gap: 3, marginTop: closure ? 3 : 0 }}>
								{slots.slice(0, MAX_DOTS).map((s, j) => {
									const cc = occColor(s);
									return <span key={`m${i}-${j}`} title={`${fmtShort(s.startTime)} · ${occLabel(s)}`} style={{ width: 6, height: 6, borderRadius: '50%', boxSizing: 'border-box', background: s.isMakeup ? '#fff' : cc.bar, border: s.isMakeup ? '1.5px solid ' + MAKEUP_OUTLINE : 'none' }} />;
								})}
								{slots.length > MAX_DOTS && <span style={{ fontSize: 9, lineHeight: '8px', color: '#9aa2b1', fontWeight: 700 }}>+{slots.length - MAX_DOTS}</span>}
								{flex && <span title="Reserve / flex day" style={{ width: '100%', textAlign: 'center', fontSize: 8, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: CAL_CHROME.flexText, lineHeight: 1 }}>Flex</span>}
							</div>
						)}
					</div>
				);
			} else {
				const more = slots.length > 3 ? `+${slots.length - 3} more` : '';
				cell = (
					<div key={'c' + i} style={{ flex: 1, minWidth: 0, minHeight: 118, borderRight: '1px solid ' + CAL_CHROME.cellBorder, borderBottom: '1px solid ' + CAL_CHROME.cellBorder, padding: '7px 8px', boxSizing: 'border-box', background: dayBg }}>
						<div style={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}>
							<div style={{ minWidth: 24, height: 24, padding: '0 6px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 999, fontWeight: 700, fontSize: '12.5px', fontFamily: SERIF, color: td ? '#fff' : numCol, background: td ? CAL_CHROME.todayText : 'transparent' }}>{dt.getDate()}</div>
						</div>
						{closure && monthClosedMarker(closure, `x${i}`)}
						{flex && flexTag(`f${i}`)}
						{slots.slice(0, 3).map((s, j) => monthChip(s, `m${i}-${j}`))}
						{more && <div style={{ fontSize: 10, color: '#9aa2b1', marginTop: 3, fontWeight: 600, paddingLeft: 2 }}>{more}</div>}
					</div>
				);
			}
			if (i % 7 === 0) weeks.push([]);
			weeks[weeks.length - 1].push(cell);
		}
		return (
			<div style={{ overflowX: isNarrow ? 'visible' : 'auto' }}>
				<div style={{ border: '1px solid #ece7e0', borderRadius: 13, overflow: 'hidden', minWidth: isNarrow ? 0 : 700 }}>
					<div style={{ display: 'flex', background: CAL_CHROME.monthHeadBg, borderBottom: '1px solid ' + CAL_CHROME.cellBorder }}>
						{[...Array(7)].map((_, i) => (
							<div key={i} style={{ flex: 1, textAlign: 'center', padding: isNarrow ? '7px 2px' : '10px 4px', fontWeight: 600, fontSize: isNarrow ? 10 : 12, letterSpacing: '.06em', textTransform: 'uppercase', color: '#8a93a4', fontFamily: SERIF }}>{isNarrow ? DOW_SH[(startDow + i) % 7][0] : DOW_SH[(startDow + i) % 7]}</div>
						))}
					</div>
					{weeks.map((wk, i) => (<div key={i} style={{ display: 'flex' }}>{wk}</div>))}
				</div>
				{isNarrow && selectedDay && renderMonthDayPanel()}
			</div>
		);
	};

	// Narrow Month only: the selected day's agenda, rendered as a contained card
	// beneath the grid (the view stays in Month). Reuses the parameterized
	// renderDay for the lesson cards — no rebuilt markup.
	const renderMonthDayPanel = () => {
		const dt = parseYmd(selectedDay);
		const n = lessonsOn(dt).length;
		return (
			<div ref={dayPanelRef} style={{ marginTop: 16, border: '1px solid ' + CAL_CHROME.cellBorder, borderRadius: 13, overflow: 'hidden', background: '#fff' }}>
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 14px', borderBottom: '1px solid ' + CAL_CHROME.headerLine, background: CAL_CHROME.monthHeadBg }}>
					<div style={{ minWidth: 0 }}>
						<div style={{ fontFamily: SERIF, fontSize: 16, fontWeight: 600, color: '#384971', lineHeight: 1.2 }}>{DOW_FU[dt.getDay()]}, {MON_FULL[dt.getMonth()]} {dt.getDate()}</div>
						<div style={{ fontSize: 12, color: '#9aa2b1', marginTop: 2 }}>{n} lesson{n === 1 ? '' : 's'}</div>
					</div>
					<button type="button" onClick={() => setSelectedDay(null)} aria-label="Close day" style={{ appearance: 'none', width: 32, height: 32, borderRadius: 8, border: '1px solid #ece7e0', background: '#fff', color: '#9aa2b1', cursor: 'pointer', flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><i className="fa-sharp fa-solid fa-xmark" /></button>
				</div>
				<div style={{ padding: '16px 14px' }}>
					{renderDay(dt)}
				</div>
			</div>
		);
	};

	// ===== DAY (agenda list) =====
	const renderDay = (dateArg = anchor) => {
		const list = lessonsOn(dateArg);
		const { closure, flex } = dayMarker(ymd(dateArg));
		if (list.length === 0) {
			if (flex) {
				return (
					<div style={{ textAlign: 'center', padding: '56px 20px', color: CAL_CHROME.flexText }}>
						<i className='fa-sharp fa-solid fa-calendar-plus' style={{ fontSize: 26, marginBottom: 12 }} />
						<div style={{ fontFamily: SERIF, fontSize: 20, marginBottom: 6 }}>Reserve day</div>
						<div style={{ fontSize: '13.5px' }}>No regular lessons — held for makeups. Families see this day as closed.</div>
					</div>
				);
			}
			if (closure) {
				const recital = closure.type === 'recital';
				return (
					<div style={{ textAlign: 'center', padding: '56px 20px', color: recital ? RECITAL_STYLE.text : CAL_CHROME.closedText }}>
						<i className={`fa-sharp fa-solid ${recital ? 'fa-star' : 'fa-calendar-xmark'}`} style={{ fontSize: 26, marginBottom: 12 }} />
						<div style={{ fontFamily: SERIF, fontSize: 20, marginBottom: 6 }}>{recital ? closure.name : 'Studio closed'}</div>
						<div style={{ fontSize: '13.5px' }}>{recital ? `${closure.location ? closure.location + ' — ' : ''}No regular lessons on recital day.` : `${closure.name} — no lessons scheduled.`}</div>
					</div>
				);
			}
			return (
				<div style={{ textAlign: 'center', padding: '64px 20px', color: '#a6adba' }}>
					<div style={{ fontFamily: SERIF, fontSize: 20, color: '#8a93a4', marginBottom: 6 }}>No lessons scheduled</div>
					<div style={{ fontSize: '13.5px' }}>Nothing is booked for this day{flex ? ' — reserve / flex day.' : '.'}</div>
				</div>
			);
		}
		return (
			<div style={{ maxWidth: 640, margin: '0 auto' }}>
				{flex && (
					<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12, padding: '8px 14px', borderRadius: 10, background: '#f7f6f3', color: CAL_CHROME.flexText, fontSize: '12.5px', fontWeight: 600 }}>
						<i className="fa-sharp fa-solid fa-calendar-plus" style={{ fontSize: 12 }} />Reserve / flex day — lessons run normally.
					</div>
				)}
				<div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
					{list.map((s, i) => {
						const isEvt = s._kind !== 'lesson';
						const c = occColor(s);
						const et = isEvt ? occAccent(s) : null;
						const st = { display: 'flex', gap: 16, alignItems: 'center', background: '#fff', border: '1px solid #ece6df', borderLeft: '5px solid ' + c.bar, borderRadius: 13, padding: '14px 18px', boxShadow: '0 1px 2px rgba(56,73,113,.06)', cursor: 'pointer' };
						if (s.isMakeup) Object.assign(st, { outline: '2px dashed ' + MAKEUP_OUTLINE, outlineOffset: -4 });
						return (
							<div key={'d' + i} onClick={() => { setSelected(s); setEditing(false); setCancelChoice(false); }} style={st}>
								<div style={{ flex: '0 0 82px', display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
									<span style={{ fontWeight: 700, color: '#384971', fontSize: 15 }}>{fmt(s.startTime)}</span>
									<span style={{ color: '#9aa2b1', fontSize: '11.5px' }}>{fmt(s.endTime)}</span>
								</div>
								<div style={{ flex: 1, minWidth: 0 }}>
									<div style={{ fontWeight: 700, color: '#384971', fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
										{isEvt && <i className={`fa-sharp fa-solid ${et.icon}`} style={{ fontSize: 13, color: et.text, flex: '0 0 auto' }} />}
										<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{occLabel(s)}</span>
									</div>
									<div style={{ display: 'flex', alignItems: 'center', gap: 11, marginTop: 4, color: '#5a6378', fontSize: '13px', flexWrap: 'wrap' }}>
										{isEvt ? (
											<>
												<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontWeight: 600, color: et.text }}>{et.name}</span>
												{s._kind === 'recital' && s.venue && <span style={{ whiteSpace: 'nowrap' }}>{s.venue}</span>}
												{/* Rotation teacher, same resolver as the week grid so the
												    two views can never disagree about who has a lesson. */}
												{rotationTeacherName(s, ymd(dateArg)) && (
													<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
														<i className='fa-sharp fa-solid fa-chalkboard-user' style={{ fontSize: 11, opacity: 0.75 }} />{rotationTeacherName(s, ymd(dateArg))}
													</span>
												)}
												{s._kind !== 'recital' && s.location && (
													<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
														<span style={{ width: 9, height: 9, borderRadius: '50%', background: getLocationColor(s.location).dot, display: 'inline-block', flex: '0 0 auto' }} />{getLocationColor(s.location).name}
													</span>
												)}
												{s._kind === 'groupClass' && (
													<span style={{ whiteSpace: 'nowrap' }}>{(s.roster || []).length}{s.capacity ? `/${s.capacity}` : ''} enrolled</span>
												)}
												{s._kind === 'rehearsal' && (s.cast || []).length > 0 && (
													<span style={{ whiteSpace: 'nowrap' }}>{(s.cast || []).length} cast</span>
												)}
											</>
										) : (
											<>
												{s.instructor && <span style={{ whiteSpace: 'nowrap' }}>{s.instructor}</span>}
												<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
													<span style={{ width: 9, height: 9, borderRadius: '50%', background: c.dot, display: 'inline-block', flex: '0 0 auto' }} />{c.name}
												</span>
												{s.isMakeup && (
													<span style={{ fontSize: 11, fontWeight: 700, color: '#b9722f', border: '1.5px dashed ' + MAKEUP_OUTLINE, borderRadius: 999, padding: '1px 10px', whiteSpace: 'nowrap' }}>Make-up</span>
												)}
											</>
										)}
									</div>
								</div>
							</div>
						);
					})}
				</div>
			</div>
		);
	};

	// ===== toolbar controls =====
	const segBtn = (label, v) => {
		const active = view === v;
		return (
			<button key={v} type="button" onClick={() => { setView(v); setSelectedDay(null); }} style={{ appearance: 'none', border: 'none', cursor: 'pointer', fontFamily: SANS, fontSize: 13, fontWeight: 600, letterSpacing: '.02em', padding: '8px 17px', borderRadius: 8, color: active ? '#fff' : '#6c7588', background: active ? '#384971' : 'transparent', transition: 'all .15s' }}>{label}</button>
		);
	};
	const filBtn = (label, key, color) => {
		const active = loc === key;
		return (
			<button key={key} type="button" onClick={() => setLoc(key)} style={{ appearance: 'none', cursor: 'pointer', fontFamily: SANS, fontSize: '12.5px', fontWeight: 600, padding: '7px 13px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 6, border: '1.5px solid ' + (active ? (color || '#384971') : '#e3ddd4'), background: active ? (color ? hexA(color, 0.1) : '#eef1f6') : '#fff', color: active ? (color ? getLocationColor(key).text : '#384971') : '#9aa2b1' }}>
				{color && <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />}{label}
			</button>
		);
	};

	// ===== detail / edit drawer =====
	const renderDrawer = () => {
		// Accent is the location color for lessons, the type color for events.
		let accent;
		if (creating && create) accent = create.kind === 'event' ? getEventColor(create.type) : getLocationColor(create.location);
		else if (editing && edit) accent = edit.kind === 'event' ? getEventColor(edit.type) : getLocationColor(edit.location);
		else accent = selected._kind && selected._kind !== 'lesson' ? occAccent(selected) : getLocationColor(selected.location);
		const selStyle = { appearance: 'none', width: '100%', height: 42, border: '1px solid #e3ddd4', borderRadius: 10, padding: '0 12px', fontFamily: SANS, fontSize: 14, fontWeight: 600, color: '#384971', background: '#fff', cursor: 'pointer' };
		const field = (label, control) => (
			<div key={label} style={{ marginBottom: 15 }}>
				<div style={{ fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', fontWeight: 700, color: '#9aa2b1', marginBottom: 7 }}>{label}</div>
				{control}
			</div>
		);
		const locBtnFor = (current, onPick) => (key) => {
			const cc = getLocationColor(key); const active = current === key;
			return (
				<button key={key} type="button" onClick={() => onPick(key)} style={{ flex: 1, appearance: 'none', cursor: 'pointer', height: 42, borderRadius: 10, fontFamily: SANS, fontSize: '13.5px', fontWeight: 600, border: '1.5px solid ' + (active ? cc.bar : '#e3ddd4'), background: active ? cc.fill : '#fff', color: active ? cc.text : '#9aa2b1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
					<span style={{ width: 9, height: 9, borderRadius: '50%', background: cc.dot }} />{cc.name}
				</button>
			);
		};
		const timeOpts = [];
		for (let m = HOUR_START * 60; m <= 20 * 60 + 30; m += 15) { const hh = Math.floor(m / 60), mm2 = m % 60; timeOpts.push(`${String(hh).padStart(2, '0')}:${String(mm2).padStart(2, '0')}`); }
		const closeBtn = (
			<button type="button" onClick={closeDrawer} aria-label="Close" style={{ appearance: 'none', width: 32, height: 32, borderRadius: 8, border: '1px solid #ece7e0', background: '#fff', color: '#9aa2b1', cursor: 'pointer', flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><i className="fa-sharp fa-solid fa-xmark" /></button>
		);
		const actionBtn = (label, kind, onClick) => {
			const base = { appearance: 'none', width: '100%', height: 42, borderRadius: 11, fontFamily: SANS, fontSize: 13.5, fontWeight: 600, letterSpacing: '.02em', cursor: 'pointer' };
			if (kind === 'primary') Object.assign(base, { border: 'none', background: '#384971', color: '#fff' });
			else if (kind === 'danger') Object.assign(base, { border: '1px solid #f0c9bc', background: '#fff', color: '#c0532c' });
			else Object.assign(base, { border: '1px solid #e3ddd4', background: '#fff', color: '#384971' });
			return <button key={label} type="button" onClick={onClick} style={base}>{label}</button>;
		};

		// ----- event-form controls (shared by event create + edit) -----
		const inputStyle = { ...selStyle, cursor: 'text' };
		const studentName = (id) => { const s = students.find((x) => x.id === id); return s ? studentDisplayName(s) : id; };
		// Roster / cast: a removable chip list + an "add student" select (the
		// students array is already loaded). Bounded + tasteful vs. a full checklist.
		const studentMultiAdd = (ids, onChange) => {
			const available = students.slice().filter((s) => !ids.includes(s.id)).sort((a, b) => studentDisplayName(a).localeCompare(studentDisplayName(b)));
			return (
				<div>
					{ids.length > 0 && (
						<div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
							{ids.map((id) => (
								<span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f3f0ea', borderRadius: 999, padding: '4px 6px 4px 11px', fontSize: 12.5, fontWeight: 600, color: '#384971' }}>
									{studentName(id)}
									<button type="button" onClick={() => onChange(ids.filter((x) => x !== id))} aria-label="Remove" style={{ appearance: 'none', border: 'none', background: 'transparent', color: '#9aa2b1', cursor: 'pointer', fontSize: 12, lineHeight: 1, padding: 0, width: 16, height: 16 }}><i className="fa-sharp fa-solid fa-xmark" /></button>
								</span>
							))}
						</div>
					)}
					<select value="" onChange={(ev) => { if (ev.target.value) onChange([...ids, ev.target.value]); }} style={selStyle}>
						<option value="">Add a student…</option>
						{available.map((s) => <option key={s.id} value={s.id}>{studentDisplayName(s)}</option>)}
					</select>
				</div>
			);
		};
		// Recital call times: repeatable label + time rows.
		const callTimesEditor = (rows, onChange) => (
			<div>
				{rows.map((ct, idx) => (
					<div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
						<input type="text" value={ct.label} placeholder="Label (e.g. Cast call)" onChange={(ev) => onChange(rows.map((r, i) => (i === idx ? { ...r, label: ev.target.value } : r)))} style={{ ...inputStyle, flex: 1 }} />
						<select value={ct.time} onChange={(ev) => onChange(rows.map((r, i) => (i === idx ? { ...r, time: ev.target.value } : r)))} style={{ ...selStyle, flex: '0 0 110px' }}>
							<option value="">Time…</option>
							{timeOpts.map((v) => <option key={v} value={v}>{fmt(v)}</option>)}
						</select>
						<button type="button" onClick={() => onChange(rows.filter((_, i) => i !== idx))} aria-label="Remove call time" style={{ appearance: 'none', width: 42, height: 42, flex: '0 0 42px', borderRadius: 10, border: '1px solid #e3ddd4', background: '#fff', color: '#9aa2b1', cursor: 'pointer' }}><i className="fa-sharp fa-solid fa-xmark" /></button>
					</div>
				))}
				<button type="button" onClick={() => onChange([...rows, { label: '', time: '' }])} style={{ appearance: 'none', height: 38, padding: '0 14px', borderRadius: 10, border: '1px dashed #d6cfc4', background: '#fff', color: '#5a6378', fontFamily: SANS, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7 }}><i className="fa-sharp fa-solid fa-plus" />Add call time</button>
			</div>
		);
		// Event-type accent buttons (create only — type drives the type-specific fields).
		const typeBtns = (current, onPick) => (
			<div style={{ display: 'flex', gap: 8 }}>
				{EVENT_TYPES.map((t) => {
					const active = current === t.value;
					return (
						<button key={t.value} type="button" onClick={() => onPick(t.value)} style={{ flex: 1, appearance: 'none', cursor: 'pointer', height: 56, borderRadius: 10, fontFamily: SANS, fontSize: 12, fontWeight: 600, border: '1.5px solid ' + (active ? t.bar : '#e3ddd4'), background: active ? t.fill : '#fff', color: active ? t.text : '#9aa2b1', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
							<i className={`fa-sharp fa-solid ${t.icon}`} style={{ fontSize: 15 }} />{t.name}
						</button>
					);
				})}
			</div>
		);
		// Shared body for the event create + edit forms (type fixed in edit).
		const eventFormBody = (st, set, { allowType }) => {
			const recital = st.type === 'recital';
			const groupClass = st.type === 'groupClass';
			const rehearsal = st.type === 'rehearsal';
			const elBtn = locBtnFor(st.location, (k) => set({ location: k }));
			return (
				<>
					{allowType && field('Type', typeBtns(st.type, (t) => set({ type: t, repeat: t === 'groupClass' ? st.repeat : false, blocksTimeOff: t === 'recital' })))}
					{field('Title', <input type="text" value={st.title} placeholder={recital ? 'Spring Recital' : groupClass ? 'Musical Theater Ensemble' : 'Cast Rehearsal'} onChange={(ev) => set({ title: ev.target.value })} style={inputStyle} />)}
					{recital
						? field('Venue', <input type="text" value={st.venue} placeholder="Venue (often off-site)" onChange={(ev) => set({ venue: ev.target.value })} style={inputStyle} />)
						: field('Location', <div style={{ display: 'flex', gap: 9 }}>{elBtn('Lakeville')}{elBtn('HLDC')}</div>)}
					{field('Date', <input type="date" value={st.date} onChange={(ev) => set({ date: ev.target.value })} style={inputStyle} />)}
					<div style={{ display: 'flex', gap: 10 }}>
						<div style={{ flex: 1 }}>{field('Start', <select value={st.startTime} onChange={(ev) => set({ startTime: ev.target.value })} style={selStyle}>{timeOpts.map((v) => <option key={v} value={v}>{fmt(v)}</option>)}</select>)}</div>
						<div style={{ flex: 1 }}>{field('End', <select value={st.endTime} onChange={(ev) => set({ endTime: ev.target.value })} style={selStyle}>{timeOpts.map((v) => <option key={v} value={v}>{fmt(v)}</option>)}</select>)}</div>
					</div>
					{groupClass && (
						<>
							{field('Capacity', <input type="number" min="0" value={st.capacity} placeholder="No limit" onChange={(ev) => set({ capacity: ev.target.value })} style={inputStyle} />)}
							{field('Roster', studentMultiAdd(st.roster || [], (r) => set({ roster: r })))}
							{field('Repeat weekly', (
								<div style={{ display: 'flex', gap: 9 }}>
									{['Single', 'Weekly'].map((lab, i) => {
										const on = (i === 1) === !!st.repeat;
										return <button key={lab} type="button" onClick={() => set({ repeat: i === 1 })} style={{ flex: 1, appearance: 'none', cursor: 'pointer', height: 42, borderRadius: 10, fontFamily: SANS, fontSize: '13.5px', fontWeight: 600, border: '1.5px solid ' + (on ? accent.bar : '#e3ddd4'), background: on ? accent.fill : '#fff', color: on ? accent.text : '#9aa2b1' }}>{lab}</button>;
									})}
								</div>
							))}
						</>
					)}
					{rehearsal && (
						<>
							{field('Cast', studentMultiAdd(st.cast || [], (r) => set({ cast: r })))}
							{field('Linked recital', (
								<select value={st.recitalId} onChange={(ev) => set({ recitalId: ev.target.value })} style={selStyle}>
									<option value="">None</option>
									{recitalEvents.map((r) => <option key={r.id} value={r.id}>{r.title || 'Recital'}</option>)}
								</select>
							))}
						</>
					)}
					{recital && field('Call times', callTimesEditor(st.callTimes || [], (r) => set({ callTimes: r })))}
					{field('Must-work day', (
						<div>
							<div style={{ display: 'flex', gap: 9 }}>
								{[['Requests allowed', false], ['Time off blocked', true]].map(([lab, val]) => {
									const on = !!st.blocksTimeOff === val;
									return <button key={lab} type="button" onClick={() => set({ blocksTimeOff: val })} style={{ flex: 1, appearance: 'none', cursor: 'pointer', height: 42, borderRadius: 10, fontFamily: SANS, fontSize: '13.5px', fontWeight: 600, border: '1.5px solid ' + (on ? accent.bar : '#e3ddd4'), background: on ? accent.fill : '#fff', color: on ? accent.text : '#9aa2b1' }}>{lab}</button>;
								})}
							</div>
							<div style={{ marginTop: 6, fontFamily: SANS, fontSize: 12, color: '#5a6378', lineHeight: 1.35 }}>
								{st.blocksTimeOff
									? 'Teachers can’t request this day off. Admins can still enter time off over it.'
									: 'Teachers may request this day off like any other.'}
							</div>
						</div>
					))}
				</>
			);
		};

		let panel;
		if (creating && create && create.kind === 'event') {
			panel = (
				<>
					<div style={{ height: 6, background: accent.bar, flex: '0 0 auto' }} />
					<div style={{ padding: '22px 26px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
						<div style={{ flex: 1, minWidth: 0 }}>
							<div style={{ fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', fontWeight: 700, color: accent.text, whiteSpace: 'nowrap' }}>New event</div>
							<div style={{ fontFamily: SERIF, fontSize: 25, fontWeight: 600, color: '#384971', marginTop: 5, lineHeight: 1.1 }}>{create.title || getEventColor(create.type).name}</div>
						</div>
						{closeBtn}
					</div>
					<div style={{ padding: '18px 26px 4px', flex: 1, overflowY: 'auto' }}>
						{eventFormBody(create, createSet, { allowType: true })}
					</div>
					<div style={{ marginTop: 'auto', padding: '18px 26px 26px', borderTop: '1px solid #f0ece6', display: 'flex', flexDirection: 'column', gap: 10 }}>
						<button type="button" onClick={saveCreateEvent} disabled={!(create.title || '').trim()} style={{ appearance: 'none', width: '100%', height: 42, borderRadius: 11, fontFamily: SANS, fontSize: 13.5, fontWeight: 600, letterSpacing: '.02em', cursor: (create.title || '').trim() ? 'pointer' : 'not-allowed', border: 'none', background: (create.title || '').trim() ? '#384971' : '#aab2c2', color: '#fff' }}>Add event</button>
						{actionBtn('Cancel', 'ghost', closeDrawer)}
					</div>
				</>
			);
		} else if (editing && edit && edit.kind === 'event') {
			panel = (
				<>
					<div style={{ height: 6, background: accent.bar, flex: '0 0 auto' }} />
					<div style={{ padding: '22px 26px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
						<div style={{ flex: 1, minWidth: 0 }}>
							<div style={{ fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', fontWeight: 700, color: accent.text, whiteSpace: 'nowrap' }}>Editing {getEventColor(edit.type).name.toLowerCase()}</div>
							<div style={{ fontFamily: SERIF, fontSize: 25, fontWeight: 600, color: '#384971', marginTop: 5, lineHeight: 1.1 }}>{edit.title || getEventColor(edit.type).name}</div>
						</div>
						{closeBtn}
					</div>
					<div style={{ padding: '18px 26px 4px', flex: 1, overflowY: 'auto' }}>
						{edit.isRecurring && <div style={{ fontSize: 12.5, color: '#7b8499', marginBottom: 14 }}>Weekly series — edits apply to every occurrence.</div>}
						{eventFormBody(edit, editSet, { allowType: false })}
					</div>
					<div style={{ marginTop: 'auto', padding: '18px 26px 26px', borderTop: '1px solid #f0ece6', display: 'flex', flexDirection: 'column', gap: 10 }}>
						{actionBtn('Save changes', 'primary', saveEditEvent)}
						{actionBtn('Back', 'ghost', () => setEditing(false))}
					</div>
				</>
			);
		} else if (selected && selected._kind && selected._kind !== 'lesson') {
			const s = selected;
			const c = accent;
			const et = getEventColor(s._kind);
			const selDate = s._date ? `${DOW_FU[s._date.getDay()]}, ${MON_FULL[s._date.getMonth()]} ${s._date.getDate()}, ${s._date.getFullYear()}` : '';
			const typeLabel = s.isRecurring ? 'Weekly recurring' : 'One-time';
			const drow = (label, val) => (
				<div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: '1px solid #f3efe9' }}>
					<span style={{ fontSize: '11.5px', color: '#9aa2b1', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase' }}>{label}</span>
					{typeof val === 'string' ? <span style={{ fontSize: 14, fontWeight: 600, color: '#384971', textAlign: 'right' }}>{val}</span> : val}
				</div>
			);
			panel = (
				<>
					<div style={{ height: 6, background: c.bar, flex: '0 0 auto' }} />
					<div style={{ padding: '22px 26px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
						<div style={{ flex: 1, minWidth: 0 }}>
							<div style={{ fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', fontWeight: 700, color: c.text, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 7 }}><i className={`fa-sharp fa-solid ${et.icon}`} />{et.name}</div>
							<div style={{ fontFamily: SERIF, fontSize: 25, fontWeight: 600, color: '#384971', marginTop: 5, lineHeight: 1.1 }}>{s.title || et.name}</div>
						</div>
						{closeBtn}
					</div>
					<div style={{ padding: '16px 26px 4px' }}>
						{drow('Time', `${fmt(s.startTime)} – ${fmt(s.endTime)}`)}
						{drow('Date', selDate)}
						{s._kind === 'recital'
							? drow('Venue', s.venue || '—')
							: drow('Location', <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 14, fontWeight: 600, color: getLocationColor(s.location).text }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: getLocationColor(s.location).dot }} />{getLocationColor(s.location).name}</span>)}
						{s._kind === 'groupClass' && drow('Enrolled', `${(s.roster || []).length}${s.capacity ? ` / ${s.capacity}` : ''}`)}
						{s._kind === 'rehearsal' && drow('Cast', `${(s.cast || []).length} student${(s.cast || []).length === 1 ? '' : 's'}`)}
						{drow('Type', typeLabel)}
						{s._kind === 'recital' && (s.callTimes || []).length > 0 && (
							<div style={{ padding: '12px 0' }}>
								<div style={{ fontSize: '11.5px', color: '#9aa2b1', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 8 }}>Call times</div>
								{s.callTimes.map((ct, i) => (
									<div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13.5, color: '#384971' }}>
										<span>{ct.label || '—'}</span><span style={{ fontWeight: 600 }}>{ct.time ? fmt(ct.time) : '—'}</span>
									</div>
								))}
							</div>
						)}
						{(s._kind === 'groupClass' || s._kind === 'rehearsal') && ((s.roster || s.cast || []).length > 0) && (
							<div style={{ padding: '12px 0' }}>
								<div style={{ fontSize: '11.5px', color: '#9aa2b1', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 8 }}>{s._kind === 'groupClass' ? 'Roster' : 'Cast'}</div>
								<div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
									{(s._kind === 'groupClass' ? s.roster : s.cast).map((id) => {
										const stu = students.find((x) => x.id === id);
										return <span key={id} style={{ background: '#f3f0ea', borderRadius: 999, padding: '4px 11px', fontSize: 12.5, fontWeight: 600, color: '#384971' }}>{stu ? studentDisplayName(stu) : id}</span>;
									})}
								</div>
							</div>
						)}
					</div>
					<div style={{ marginTop: 'auto', padding: '18px 26px 26px', borderTop: '1px solid #f0ece6', display: 'flex', flexDirection: 'column', gap: 10 }}>
						{!canManageEvents ? (
							<div style={{ fontSize: 12.5, color: '#9aa2b1', textAlign: 'center' }}>Events are managed by an admin.</div>
						) : cancelChoice ? (
							<>
								<div style={{ fontSize: 12.5, color: '#7b8499', textAlign: 'center', marginBottom: 2 }}>End the series going forward, or delete the whole series?</div>
								{actionBtn('End series going forward', 'danger', stopEventSeries)}
								{actionBtn('Delete entire series', 'danger', removeEvent)}
								{actionBtn('Keep event', 'ghost', () => setCancelChoice(false))}
							</>
						) : (
							<>
								{actionBtn('Edit event', 'primary', startEditEvent)}
								{actionBtn('Delete event', 'danger', s.isRecurring ? () => setCancelChoice(true) : removeEvent)}
							</>
						)}
					</div>
				</>
			);
		} else if (creating && create) {
			const locBtn = locBtnFor(create.location, (k) => createSet({ location: k }));
			const warn = offWarn(create.instructor, create.date);
			const mkPill = (label, on, onClick) => (
				<button type="button" onClick={onClick} style={{ flex: 1, appearance: 'none', cursor: 'pointer', height: 42, borderRadius: 10, fontFamily: SANS, fontSize: '13.5px', fontWeight: 600, border: '1.5px solid ' + (on ? MAKEUP_OUTLINE : '#e3ddd4'), background: on ? '#fdf3e7' : '#fff', color: on ? '#b9722f' : '#9aa2b1' }}>{label}</button>
			);
			panel = (
				<>
					<div style={{ height: 6, background: accent.bar, flex: '0 0 auto' }} />
					<div style={{ padding: '22px 26px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
						<div style={{ flex: 1, minWidth: 0 }}>
							<div style={{ fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', fontWeight: 700, color: accent.text, whiteSpace: 'nowrap' }}>New lesson</div>
							<div style={{ fontFamily: SERIF, fontSize: 25, fontWeight: 600, color: '#384971', marginTop: 5, lineHeight: 1.1 }}>{create.studentName || 'Add a lesson'}</div>
						</div>
						{closeBtn}
					</div>
					<div style={{ padding: '18px 26px 4px', flex: 1, overflowY: 'auto' }}>
						{field('Student', (
							<select value={create.studentId} onChange={(ev) => onPickStudent(ev.target.value)} style={selStyle}>
								<option value="">Select a student…</option>
								{students.slice().sort((a, b) => studentDisplayName(a).localeCompare(studentDisplayName(b))).map((st) => (
									<option key={st.id} value={st.id}>{studentDisplayName(st)}{st.instrument ? ` · ${st.instrument}` : ''}</option>
								))}
							</select>
						))}
						{field('Category', (
							<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
								{LESSON_CATEGORIES.map((cat) => {
									const on = create.instrument === cat;
									return <button key={cat} type="button" onClick={() => onPickCategory(cat)} style={{ appearance: 'none', cursor: 'pointer', height: 42, borderRadius: 10, fontFamily: SANS, fontSize: '13px', fontWeight: 600, border: '1.5px solid ' + (on ? accent.bar : '#e3ddd4'), background: on ? accent.fill : '#fff', color: on ? accent.text : '#9aa2b1' }}>{cat}</button>;
								})}
							</div>
						))}
						{field('Location', <div style={{ display: 'flex', gap: 9 }}>{locBtn('Lakeville')}{locBtn('HLDC')}</div>)}
						{field('Instructor', (
							<select value={create.instructor} onChange={(ev) => createSet({ instructor: ev.target.value })} style={selStyle}>
								<option value="">Unassigned</option>
								{instructorOptions.map((n) => <option key={n} value={n}>{n}</option>)}
							</select>
						))}
						{field('Date', (
							<input type="date" value={create.date} onChange={(ev) => onCreateDate(ev.target.value)} style={{ ...selStyle, cursor: 'text' }} />
						))}
						{field('Start time', (
							<select value={create.startTime} onChange={(ev) => createSet({ startTime: ev.target.value })} style={selStyle}>
								{timeOpts.map((v) => <option key={v} value={v}>{fmt(v)}</option>)}
							</select>
						))}
						<div style={{ fontSize: 13, color: '#7b8499', marginTop: -2, marginBottom: 16 }}>Ends at {fmt(addMinStr(create.startTime, create.duration))} · {create.duration}-minute {create.instrument ? create.instrument.toLowerCase() + ' ' : ''}lesson</div>
						{field('Type', <div style={{ display: 'flex', gap: 9 }}>{mkPill('Regular', !create.isMakeup, () => createSet({ isMakeup: false }))}{mkPill('Make-up', !!create.isMakeup, () => createSet({ isMakeup: true, repeat: false }))}</div>)}
						{(() => {
							const repeatOff = create.isMakeup || create.instrument === 'Private Coaching';
							return field('Repeat weekly', (
								<div>
									<div style={{ display: 'flex', gap: 9, opacity: repeatOff ? 0.5 : 1, pointerEvents: repeatOff ? 'none' : 'auto' }}>
										{['Single', 'Weekly'].map((lab, i) => {
											const on = (i === 1) === !!create.repeat;
											return <button key={lab} type="button" onClick={() => createSet({ repeat: i === 1 })} style={{ flex: 1, appearance: 'none', cursor: 'pointer', height: 42, borderRadius: 10, fontFamily: SANS, fontSize: '13.5px', fontWeight: 600, border: '1.5px solid ' + (on ? accent.bar : '#e3ddd4'), background: on ? accent.fill : '#fff', color: on ? accent.text : '#9aa2b1' }}>{lab}</button>;
										})}
									</div>
									{repeatOff && <div style={{ fontSize: 12, color: '#9aa2b1', marginTop: 7 }}>{create.instrument === 'Private Coaching' ? 'Private Coaching is a single ad-hoc session.' : 'Make-up lessons are one-off.'}</div>}
								</div>
							));
						})()}
						{/* A weekly series belongs to a WEEKDAY COHORT, so its first lesson is
						    a canon date, not the day you happen to be looking at. Shown only
						    when Weekly is on, and editable — a mid-season joiner really does
						    start later. Leaving this implicit is what put three students on
						    the August calendar (card LSJH6ru7wBwFjiybAtl7). */}
						{create.repeat && !create.isMakeup && create.instrument !== 'Private Coaching' && field('First lesson', (
							<div>
								<input type="date" value={create.startDate} onChange={(ev) => createSet({ startDate: ev.target.value })} style={{ ...selStyle, cursor: 'text' }} />
								<div style={{ fontSize: 12, color: '#9aa2b1', marginTop: 7 }}>
									{create.startDate === canonStartFor(create.date)
										? `Fall cohort start for ${DOW_FU[parseYmd(create.date).getDay()]}s. Runs weekly through ${SEASON_END}.`
										: `Custom start — the ${DOW_FU[parseYmd(create.date).getDay()]} cohort begins ${canonStartFor(create.date)}. Runs weekly through ${SEASON_END}.`}
								</div>
							</div>
						))}
						{warn && (
							<div style={{ marginTop: 12, padding: '10px 12px', background: '#fdf3e7', border: '1px solid #f3d6b0', borderRadius: 10, fontSize: 12.5, color: '#9a6212', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
								<i className="fa-sharp fa-solid fa-triangle-exclamation" style={{ marginTop: 2 }} />{warn}
							</div>
						)}
					</div>
					<div style={{ marginTop: 'auto', padding: '18px 26px 26px', borderTop: '1px solid #f0ece6', display: 'flex', flexDirection: 'column', gap: 10 }}>
						<button type="button" onClick={saveCreate} disabled={!create.studentId} style={{ appearance: 'none', width: '100%', height: 42, borderRadius: 11, fontFamily: SANS, fontSize: 13.5, fontWeight: 600, letterSpacing: '.02em', cursor: create.studentId ? 'pointer' : 'not-allowed', border: 'none', background: create.studentId ? '#384971' : '#aab2c2', color: '#fff' }}>Add lesson</button>
						{actionBtn('Cancel', 'ghost', closeDrawer)}
					</div>
				</>
			);
		} else if (editing && edit) {
			const locBtn = locBtnFor(edit.location, (k) => editSet({ location: k }));
			const warn = offWarn(edit.instructor, edit.date);
			panel = (
				<>
					<div style={{ height: 6, background: accent.bar, flex: '0 0 auto' }} />
					<div style={{ padding: '22px 26px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
						<div style={{ flex: 1, minWidth: 0 }}>
							<div style={{ fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', fontWeight: 700, color: accent.text, whiteSpace: 'nowrap' }}>Editing lesson</div>
							<div style={{ fontFamily: SERIF, fontSize: 25, fontWeight: 600, color: '#384971', marginTop: 5, lineHeight: 1.1 }}>{edit.student}</div>
						</div>
						{closeBtn}
					</div>
					<div style={{ padding: '18px 26px 4px', flex: 1, overflowY: 'auto' }}>
						{field('Category', (
							<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
								{LESSON_CATEGORIES.map((cat) => {
									const on = edit.instrument === cat;
									return <button key={cat} type="button" onClick={() => onEditCategory(cat)} style={{ appearance: 'none', cursor: 'pointer', height: 42, borderRadius: 10, fontFamily: SANS, fontSize: '13px', fontWeight: 600, border: '1.5px solid ' + (on ? accent.bar : '#e3ddd4'), background: on ? accent.fill : '#fff', color: on ? accent.text : '#9aa2b1' }}>{cat}</button>;
								})}
							</div>
						))}
						{field('Location', <div style={{ display: 'flex', gap: 9 }}>{locBtn('Lakeville')}{locBtn('HLDC')}</div>)}
						{field('Instructor', (
							<select value={edit.instructor} onChange={(ev) => editSet({ instructor: ev.target.value })} style={selStyle}>
								<option value="">Unassigned</option>
								{instructorOptions.map((n) => <option key={n} value={n}>{n}</option>)}
							</select>
						))}
						{field('Date', (
							<input type="date" value={edit.date} onChange={(ev) => editSet({ date: ev.target.value })} style={{ ...selStyle, cursor: 'text' }} />
						))}
						{field('Start time', (
							<select value={edit.startTime} onChange={(ev) => editSet({ startTime: ev.target.value, endTime: addMinStr(ev.target.value, edit.dur) })} style={selStyle}>
								{timeOpts.map((v) => <option key={v} value={v}>{fmt(v)}</option>)}
							</select>
						))}
						<div style={{ fontSize: 13, color: '#7b8499', marginTop: -2 }}>Ends at {fmt(edit.endTime)} · {edit.dur}-minute lesson</div>
						{warn && (
							<div style={{ marginTop: 12, padding: '10px 12px', background: '#fdf3e7', border: '1px solid #f3d6b0', borderRadius: 10, fontSize: 12.5, color: '#9a6212', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
								<i className="fa-sharp fa-solid fa-triangle-exclamation" style={{ marginTop: 2 }} />{warn}
							</div>
						)}
					</div>
					<div style={{ marginTop: 'auto', padding: '18px 26px 26px', borderTop: '1px solid #f0ece6', display: 'flex', flexDirection: 'column', gap: 10 }}>
						{actionBtn('Save changes', 'primary', saveEdit)}
						{actionBtn('Back', 'ghost', () => setEditing(false))}
					</div>
				</>
			);
		} else {
			const s = selected;
			const c = accent;
			const selDate = s._date ? `${DOW_FU[s._date.getDay()]}, ${MON_FULL[s._date.getMonth()]} ${s._date.getDate()}, ${s._date.getFullYear()}` : '';
			const kicker = s.isMakeup ? 'Make-up lesson' : (s.instrument ? `${cap(s.instrument)} lesson` : 'Lesson');
			const typeLabel = s.isMakeup ? 'Make-up (one-off)' : (s.isRecurring ? 'Weekly recurring' : 'One-time');
			const drow = (label, val) => (
				<div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: '1px solid #f3efe9' }}>
					<span style={{ fontSize: '11.5px', color: '#9aa2b1', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase' }}>{label}</span>
					{typeof val === 'string' ? <span style={{ fontSize: 14, fontWeight: 600, color: '#384971', textAlign: 'right', whiteSpace: 'nowrap' }}>{val}</span> : val}
				</div>
			);
			panel = (
				<>
					<div style={{ height: 6, background: c.bar, flex: '0 0 auto' }} />
					<div style={{ padding: '22px 26px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
						<div style={{ flex: 1, minWidth: 0 }}>
							<div style={{ fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', fontWeight: 700, color: c.text, whiteSpace: 'nowrap' }}>{kicker}</div>
							<div style={{ fontFamily: SERIF, fontSize: 25, fontWeight: 600, color: '#384971', marginTop: 5, lineHeight: 1.1 }}>{s.studentName}</div>
						</div>
						{closeBtn}
					</div>
					<div style={{ padding: '16px 26px 4px' }}>
						{drow('Time', `${fmt(s.startTime)} – ${fmt(s.endTime)}`)}
						{drow('Date', selDate)}
						{drow('Location', <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 14, fontWeight: 600, color: c.text }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: c.dot }} />{c.name}</span>)}
						{drow('Instructor', s.instructor || '—')}
						{drow('Type', typeLabel)}
					</div>
					<div style={{ marginTop: 'auto', padding: '18px 26px 26px', borderTop: '1px solid #f0ece6', display: 'flex', flexDirection: 'column', gap: 10 }}>
						{cancelChoice ? (
							<>
								<div style={{ fontSize: 12.5, color: '#7b8499', textAlign: 'center', marginBottom: 2 }}>Cancel just this date, or the whole weekly series?</div>
								{actionBtn('Cancel this lesson only', 'danger', cancelLesson)}
								{actionBtn('Cancel this series', 'danger', cancelSeries)}
								{actionBtn('Keep lesson', 'ghost', () => setCancelChoice(false))}
							</>
						) : (
							<>
								{actionBtn('Reschedule', 'primary', startEdit)}
								{actionBtn('Edit details', 'ghost', startEdit)}
								{actionBtn(s.isMakeup ? 'Cancel make-up' : 'Cancel this lesson', 'danger', (s.isRecurring && !s.isMakeup) ? () => setCancelChoice(true) : cancelLesson)}
							</>
						)}
					</div>
				</>
			);
		}

		return (
			<div style={{ position: 'absolute', inset: 0, zIndex: 50, display: 'flex', justifyContent: 'flex-end' }}>
				<div onClick={closeDrawer} style={{ position: 'absolute', inset: 0, background: 'rgba(56,73,113,.20)' }} />
				<div style={{ position: 'relative', width: 376, maxWidth: '92%', height: '100%', background: '#fff', boxShadow: '-18px 0 44px -22px rgba(56,73,113,.55)', display: 'flex', flexDirection: 'column' }}>{panel}</div>
			</div>
		);
	};

	return (
		<div style={{ position: 'relative', background: '#fff', borderRadius: 18, boxShadow: '0 18px 40px -24px rgba(56,73,113,.45)', border: '1px solid #efece7', overflow: 'hidden', fontFamily: SANS, color: '#384971' }}>
			{/* summary line */}
			<div style={{ display: 'flex', justifyContent: isNarrow ? 'flex-start' : 'flex-end', padding: isNarrow ? '10px 12px 0' : '12px 22px 0', color: '#7b8499', fontSize: isNarrow ? '11.5px' : '13px', lineHeight: 1.5 }}>{!loading && summary}</div>

			{/* TOOLBAR */}
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: isNarrow ? 10 : 18, flexWrap: 'wrap', padding: isNarrow ? '10px 12px 14px' : '12px 22px 18px', borderBottom: '1px solid #f0ece6' }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: isNarrow ? 10 : 16, flexWrap: 'wrap' }}>
					<div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
						<button type="button" onClick={() => navigate(-1)} aria-label="Previous" style={{ appearance: 'none', width: 34, height: 34, borderRadius: 9, border: '1px solid #e7e2db', background: '#fff', color: '#5a6378', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><i className="fa-sharp fa-solid fa-chevron-left" /></button>
						<button type="button" onClick={() => { setAnchor(new Date()); setSelectedDay(null); }} style={{ appearance: 'none', height: 34, padding: '0 15px', borderRadius: 9, border: '1px solid #e7e2db', background: '#fff', color: '#384971', fontFamily: SANS, fontSize: 13, fontWeight: 600, letterSpacing: '.02em', cursor: 'pointer' }}>Today</button>
						<button type="button" onClick={() => navigate(1)} aria-label="Next" style={{ appearance: 'none', width: 34, height: 34, borderRadius: 9, border: '1px solid #e7e2db', background: '#fff', color: '#5a6378', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><i className="fa-sharp fa-solid fa-chevron-right" /></button>
					</div>
					<div style={{ fontFamily: SERIF, fontWeight: 600, fontSize: isNarrow ? 17 : 21, color: '#384971', minWidth: isNarrow ? 0 : 200 }}>{dateLabel}</div>
				</div>
				<div style={{ display: 'flex', alignItems: 'center', gap: isNarrow ? 8 : 12, flexWrap: 'wrap' }}>
					<div style={{ display: 'inline-flex', gap: 7 }}>
						{filBtn('All', 'all', null)}
						{filBtn('Lakeville', 'Lakeville', '#1d83d4')}
						{filBtn('Headline', 'HLDC', '#e0699a')}
					</div>
					<div style={{ display: 'inline-flex', gap: 3, background: '#f3f0ea', border: '1px solid #e7e2db', borderRadius: 11, padding: 3 }}>
						{segBtn('Day', 'day')}{segBtn('Week', 'week')}{segBtn('Month', 'month')}
					</div>
					<button type="button" onClick={openCreate} style={{ appearance: 'none', height: 38, padding: '0 17px', borderRadius: 10, border: 'none', background: '#384971', color: '#fff', fontFamily: SANS, fontSize: '13.5px', fontWeight: 600, letterSpacing: '.02em', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7 }}><i className="fa-sharp fa-solid fa-plus" />Add lesson</button>
						{canManageEvents && (
							<button type="button" onClick={openCreateEvent} style={{ appearance: 'none', height: 38, padding: '0 17px', borderRadius: 10, border: '1px solid #d8d2c8', background: '#fff', color: '#384971', fontFamily: SANS, fontSize: '13.5px', fontWeight: 600, letterSpacing: '.02em', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7 }}><i className="fa-sharp fa-solid fa-calendar-plus" />Add event</button>
						)}
					<button type="button" onClick={() => setPrefsOpen((o) => !o)} title="Calendar preferences" style={{ appearance: 'none', width: 38, height: 38, borderRadius: 10, border: '1px solid #e7e2db', background: '#fff', color: '#5a6378', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><i className="fa-sharp fa-solid fa-gear" /></button>
				</div>
			</div>

			{/* PREFERENCES popover */}
			{prefsOpen && (
				<div style={{ position: 'absolute', inset: 0, zIndex: 45 }}>
					<div onClick={() => setPrefsOpen(false)} style={{ position: 'absolute', inset: 0 }} />
					<div style={{ position: 'absolute', top: 72, right: 22, width: 266, background: '#fff', border: '1px solid #ece7e0', borderRadius: 14, boxShadow: '0 18px 44px -16px rgba(56,73,113,.5)', padding: '17px 18px' }}>
						<div style={{ fontFamily: SERIF, fontSize: 17, fontWeight: 600, color: '#384971', marginBottom: 13 }}>Calendar preferences</div>
						<div style={{ fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', fontWeight: 700, color: '#9aa2b1', marginBottom: 8 }}>Week starts on</div>
						<div style={{ display: 'flex', gap: 3, background: '#f3f0ea', border: '1px solid #e7e2db', borderRadius: 10, padding: 3 }}>
							{['mon', 'sun'].map((v) => {
								const active = weekStart === v;
								return <button key={v} type="button" onClick={() => setWeekStartPref(v)} style={{ flex: 1, appearance: 'none', border: 'none', cursor: 'pointer', fontFamily: SANS, fontSize: 13, fontWeight: 600, padding: '8px 0', borderRadius: 8, color: active ? '#fff' : '#6c7588', background: active ? '#384971' : 'transparent' }}>{v === 'mon' ? 'Monday' : 'Sunday'}</button>;
							})}
						</div>
						<div style={{ fontSize: 12, color: '#9aa2b1', marginTop: 11, lineHeight: 1.5 }}>Applies to the Week and Month grids. Saved to your profile.</div>
					</div>
				</div>
			)}

			{/* BODY */}
			<div style={{ padding: isNarrow ? '14px 10px 18px' : '20px 22px 24px' }}>
				{loading ? (
					<div style={{ textAlign: 'center', padding: '64px 20px', color: '#a6adba' }}>Loading…</div>
				) : (
					<>
						{view === 'week' && (weekAsAgenda ? renderDay() : renderWeek())}
						{view === 'month' && renderMonth()}
						{view === 'day' && renderDay()}
					</>
				)}
			</div>

			{/* LEGEND */}
			<div style={{ display: 'flex', alignItems: 'center', gap: isNarrow ? 12 : 22, flexWrap: 'wrap', padding: isNarrow ? '12px' : '14px 22px', borderTop: '1px solid #f0ece6', background: '#fcfaf7' }}>
				{LOCATIONS.map((l) => (
					<span key={l.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '12.5px', color: '#5a6378', fontWeight: 500 }}>
						<span style={{ width: 13, height: 13, borderRadius: 4, background: l.fill, border: '1.5px solid ' + l.bar, borderLeftWidth: 4, display: 'inline-block', boxSizing: 'border-box' }} />{l.name}{l.value === 'HLDC' ? ' (HLDC)' : ''}
					</span>
				))}
				<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '12.5px', color: '#5a6378', fontWeight: 500 }}>
					<span style={{ width: 13, height: 13, borderRadius: 4, border: '2px dashed ' + MAKEUP_OUTLINE, display: 'inline-block', boxSizing: 'border-box' }} />Make-up lesson
				</span>
				<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '12.5px', color: '#5a6378', fontWeight: 500 }}>
					<span style={{ width: 13, height: 13, borderRadius: 4, background: CAL_CHROME.todayBg, border: '1.5px solid ' + CAL_CHROME.todayText, display: 'inline-block', boxSizing: 'border-box' }} />Today
				</span>
				{EVENT_TYPES.map((t) => (
					<span key={t.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: '12.5px', color: '#5a6378', fontWeight: 500 }}>
						<i className={`fa-sharp fa-solid ${t.icon}`} style={{ fontSize: 11, color: t.bar }} />{t.name}
					</span>
				))}
				<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '12.5px', color: '#5a6378', fontWeight: 500 }}>
					<span style={{ width: 13, height: 13, borderRadius: 4, background: CAL_CHROME.closedBg, border: '1.5px solid ' + CAL_CHROME.closedText, display: 'inline-block', boxSizing: 'border-box' }} />Studio closed
				</span>
				<span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: '12.5px', color: '#5a6378', fontWeight: 500 }}>
					<span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: CAL_CHROME.flexText }}>Flex</span>reserve day
				</span>
			</div>

			{/* DETAIL / EDIT DRAWER */}
			{(selected || creating) && renderDrawer()}

			{/* UNDO / status TOAST */}
			{toast && (
				<div style={{ position: 'absolute', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 60, display: 'flex', alignItems: 'center', gap: 16, background: '#384971', color: '#fff', padding: '12px 16px 12px 18px', borderRadius: 12, boxShadow: '0 16px 34px -12px rgba(56,73,113,.6)' }}>
					<span style={{ fontSize: '13.5px', fontWeight: 500 }}>{toast.msg}</span>
					{toast.undoId && (
						<button type="button" onClick={() => undoCancel(toast.undoId)} style={{ appearance: 'none', border: 'none', background: 'transparent', color: '#8fd3ca', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: SANS }}>Undo</button>
					)}
				</div>
			)}
		</div>
	);
};

export default CalendarTab;
